<template>
  <div class="w-100">
    <b-alert
      v-if="$store.state.contractsToCompare.length == 0"
      dismissible
      show
      variant="danger"
    >
      <h6>Verträge auswählen</h6>
      <p>Es wurden keine Verträge zum vergleichen ausgewählt</p>
    </b-alert>
    <div v-else>
      <u-loader
        v-if="isLoading"
        :text="
          'Geladene Verträge: ' +
            loadedContracts.length +
            '/' +
            this.$store.state.contractsToCompare.length
        "
      />
      <div v-else>
        <ComparisonExport
          :csv-definition="csvContent"
          :csv-export-settings="csvExportSettings"
          :csv-file-name="csvFileName"
          :csv-tenant-export-settings="csvTenantExportSettings"
          :loaded-contracts="loadedContracts"
          :selected-language="selectedLanguage"
          :pdf-content="pdfContent"
          :pdf-file-name="pdfExportName"
        />
        <div class="w-100 d-inline-block d-print-none">
          <v-select
            v-model="selectedLanguage"
            :items="languages"
            class="w-25 float-right"
            item-text="label"
            item-value="language"
          />
        </div>
        <div class="compareblaTableWrapper">
          <table
            :style="{ 'min-width': 600 * loadedContracts.length + 1 + 'px' }"
            class="table compareblaTable"
          >
            <thead>
              <tr>
                <td />
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  <router-link
                    :to="
                      '/contract-overview/' +
                        contract.ContractInfo.contractNumber +
                        '/' +
                        contract.VersionInfo.number
                    "
                  >
                    {{
                      getContentByLanguage(
                        contract.VersionInfo.versionContents,
                        "nameShort",
                        selectedLanguage,
                        true,
                      ) +
                        ` (Edition: ${
                          contract.EditionInfo.id != null
                            ? contract.EditionInfo.id.toString()
                            : ""
                        })`
                    }}
                  </router-link>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Name</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    getContentByLanguage(
                      contract.VersionInfo.versionContents,
                      "value",
                      selectedLanguage,
                      false,
                    )
                  }}
                </td>
              </tr>
              <tr>
                <td>Kurzname</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    getContentByLanguage(
                      contract.VersionInfo.versionContents,
                      "nameShort",
                      selectedLanguage,
                      false,
                    )
                  }}
                </td>
              </tr>
              <tr>
                <td>Vertragsnummer</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ contract.ContractInfo.contractNumber }}
                </td>
              </tr>
              <tr>
                <td>Version</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.VersionInfo.number != null
                      ? contract.VersionInfo.number.toString()
                      : ""
                  }}
                </td>
              </tr>
              <tr>
                <td>Edition</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.EditionInfo.id != null
                      ? contract.EditionInfo.id.toString()
                      : ""
                  }}
                </td>
              </tr>
              <tr>
                <td>Originalsprache</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ contract.ContractInfo.contractLanguage }}
                </td>
              </tr>
              <tr>
                <td>Vertragstyp</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contractTypesfilter(
                      contractTypes,
                      contract.ContractInfo.contractTypeId,
                      "id",
                      "name",
                    )
                  }}
                </td>
              </tr>
              <tr>
                <td>Tags (Vertrag)</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ contract.ContractInfo.tags }}
                </td>
              </tr>
              <tr>
                <td>Kommentare (Vertrag)</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.ContractInfo.contractComments
                      .map((x) => x.commentText)
                      .join("; ")
                  }}
                </td>
              </tr>
              <tr>
                <td>Tags (Version)</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ contract.VersionInfo.tags }}
                </td>
              </tr>
              <tr>
                <td>Version Status</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.VersionInfo.versionState === 0
                      ? "-"
                      : versionStates[
                        contract.VersionInfo.versionState != null
                          ? contract.VersionInfo.versionState.toString()
                          : ""
                      ] || ""
                  }}
                </td>
              </tr>
              <tr>
                <td>Verantwortung Erfassung</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contractTypesfilter(
                      users,
                      contract.VersionInfo.responsableUserId,
                      "id",
                      "name",
                    )
                  }}
                </td>
              </tr>
              <tr>
                <td>Kontrolle Erfassung</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contractTypesfilter(
                      users,
                      contract.VersionInfo.controllerUserId,
                      "id",
                      "name",
                    )
                  }}
                </td>
              </tr>
              <tr>
                <td>Vertrag wird übersetzt</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.VersionInfo.translatedToLanguages
                      .map((x) => x.language)
                      .join(", ")
                  }}
                </td>
              </tr>
              <tr>
                <td>Verantwortlicher</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ contract.ContractInfo.contractResponsible }}
                </td>
              </tr>
              <tr>
                <td>Anzahl unterstellter Betriebe</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    CreateTextForYearValue(
                      contract.ContractInfo.companiesUnderContract,
                    )
                  }}
                </td>
              </tr>
              <tr>
                <td>Anzahl unterstellter ArbeitnehmerInnen</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    CreateTextForYearValue(
                      contract.ContractInfo.employeesUnderContract,
                    )
                  }}
                </td>
              </tr>

              <tr>
                <td>Änderung gültig ab</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ formatDate(contract.VersionInfo.validFrom) }}
                </td>
              </tr>
              <tr>
                <td>Änderung gültig bis</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ formatDate(contract.VersionInfo.validTo) }}
                </td>
              </tr>
              <tr>
                <td>AVE</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  <template v-if="contract.VersionInfo.ave">
                    {{
                      contract.VersionInfo.ave.aveStart ||
                        contract.VersionInfo.ave.aveEnd ||
                        contract.VersionInfo.ave.ablaufdatum
                        ? "Ja"
                        : "Nein"
                    }}
                  </template>
                  <template v-else>
                    Nein
                  </template>
                </td>
              </tr>

              <tr>
                <td>Ave-Status</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ aveStatus[contract.VersionInfo.ave.aveState] }}
                </td>
              </tr>
              <tr>
                <td>AVE Änderungen gültig ab</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.VersionInfo.ave &&
                      contract.VersionInfo.ave.aveStart
                      ? formatDate(contract.VersionInfo.ave.aveStart)
                      : ""
                  }}
                </td>
              </tr>
              <tr>
                <td>AVE Änderungen gültig bis</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.VersionInfo.ave && contract.VersionInfo.ave.aveEnd
                      ? formatDate(contract.VersionInfo.ave.aveEnd)
                      : ""
                  }}
                </td>
              </tr>
              <tr>
                <td>Künstliche Verlängerung bis</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.VersionInfo.ave &&
                      contract.VersionInfo.ave.ablaufdatum
                      ? formatDate(contract.VersionInfo.ave.ablaufdatum)
                      : ""
                  }}
                </td>
              </tr>

              <tr>
                <td>Anhang 1</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ contract.VersionInfo.anhang1 ? "Ja" : "Nein" }}
                </td>
              </tr>

              <tr>
                <td>Anhang 1 bis</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.VersionInfo.anhang1ExtendedUntil
                      ? formatDate(contract.VersionInfo.anhang1ExtendedUntil)
                      : ""
                  }}
                </td>
              </tr>

              <tr>
                <td>GAV-Details anzeigen</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{
                    contract.EditionInfo.vertragsdatenVisible ? "Ja" : "Nein"
                  }}
                </td>
              </tr>
              <tr>
                <td>Lohnrechner anzeigen</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ contract.EditionInfo.lohnrechnerVisible ? "Ja" : "Nein" }}
                </td>
              </tr>
              <tr>
                <td>Üblicher Lohn anzeigen</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ contract.EditionInfo.uelVisible ? "Ja" : "Nein" }}
                </td>
              </tr>
              <tr>
                <td>Kommentar zur Edition</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  {{ contract.EditionInfo.komentar }}
                </td>
              </tr>
              <tr>
                <td>Noga-Branchen</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  <!-- eslint-disable vue/no-v-html -->
                  <div
                    v-html="CreateTextForNogaBranches(contract.nogaBranches)"
                  />
                  <!-- eslint-enable vue/no-v-html -->
                </td>
              </tr>
              <tr>
                <td>Kantone</td>
                <td
                  v-for="(contract, index) in loadedContracts"
                  :key="index"
                >
                  <!-- eslint-disable vue/no-v-html -->
                  <div v-html="contract.cantons" />
                  <!-- eslint-enable vue/no-v-html -->
                </td>
              </tr>
              <template v-for="(tenant, index) in tenants">
                <tr
                  :key="`${index}bez`"
                  class="newSection"
                >
                  <td>
                    {{ tenant.bezeichnung }}
                  </td>
                  <td
                    v-for="(contract, tenantIndex) in loadedContracts"
                    :key="tenantIndex"
                  />
                </tr>

                <tr :key="`${index}pubdat`">
                  <td>Publikationsdatum</td>
                  <td
                    v-for="(contract, tenantIndex) in loadedContracts"
                    :key="tenantIndex"
                  >
                    {{
                      formatDate(
                        getPublicationProperty(
                          contract.PublicationInfo,
                          tenant.id,
                          "publicationDate",
                        ),
                      )
                    }}
                  </td>
                </tr>
                <tr :key="`${index}from`">
                  <td>Gültig ab</td>
                  <td
                    v-for="(contract, tenantIndex) in loadedContracts"
                    :key="tenantIndex"
                  >
                    {{
                      formatDate(
                        getPublicationProperty(
                          contract.PublicationInfo,
                          tenant.id,
                          "validFrom",
                        ),
                      )
                    }}
                  </td>
                </tr>
                <tr :key="`${index}disname`">
                  <td>Anzeigename</td>
                  <td
                    v-for="(contract, tenantIndex) in loadedContracts"
                    :key="tenantIndex"
                  >
                    {{
                      getPublicationContentProperty(
                        contract.PublicationInfo,
                        tenant.id,
                        "value",
                        selectedLanguage,
                      )
                    }}
                  </td>
                </tr>
                <tr :key="`${index}short`">
                  <td>Kurzname Tenant</td>
                  <td
                    v-for="(contract, tenantIndex) in loadedContracts"
                    :key="tenantIndex"
                  >
                    {{
                      getPublicationContentProperty(
                        contract.PublicationInfo,
                        tenant.id,
                        "shortName",
                        selectedLanguage,
                      )
                    }}
                  </td>
                </tr>
                <tr :key="`${index}comm`">
                  <td>Kommentar</td>
                  <td
                    v-for="(contract, tenantIndex) in loadedContracts"
                    :key="tenantIndex"
                  >
                    {{
                      getPublicationContentProperty(
                        contract.PublicationInfo,
                        tenant.id,
                        "comment",
                        selectedLanguage,
                      )
                    }}
                  </td>
                </tr>
                <tr :key="`${index}new`">
                  <td>Was ist neu</td>
                  <td
                    v-for="(contract, tenantIndex) in loadedContracts"
                    :key="tenantIndex"
                  >
                    {{
                      getPublicationContentProperty(
                        contract.PublicationInfo,
                        tenant.id,
                        "news",
                        selectedLanguage,
                      )
                    }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="w-100 d-inline-block d-print-none">
          <v-select
            v-model="selectedLanguage"
            :items="languages"
            class="w-25 float-right"
            item-text="label"
            item-value="language"
          />
        </div>
        <CompareCriterionsMultiple
          v-if="this.$store.state.contractsToCompare.length > 0"
          :contracts="this.$store.state.contractsToCompare"
          :fallback-languages="fallbackLanguages"
          :selected-language="selectedLanguage"
          class="mt-4"
          @csvGenerated="updatedCsvDefinition"
          @pdfGenerated="updatedPdfDefinition"
        />
        <div class="w-100 d-inline-block d-print-none">
          <v-select
            v-model="selectedLanguage"
            :items="languages"
            class="w-25 float-right"
            item-text="label"
            item-value="language"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { GetSinlgeEdition } from "@/services/contract.edition.service.js";
import {
  GetVersionInformation,
  GetContractInformation,
  GetVersionStatus,
} from "@/services/contract.service.js";
import { GetAllUsers } from "@/services/user.service.js";
import { GetAllPublications } from "@/services/contract.tenants.service.js";
import { GetCantons } from "@/services/contract.edition.cantons.service.js";
import { GetFallbackLanguages } from "@/services/contract.edition.translation.service.js";
import { GetNogaBranchesForEdition } from "@/services/contract.edition.nogaBranches.service.js";
import { StammdatenMixin } from "@/mixins/StammdatenMixin";
import { CreateTopLevelBranchenList } from "@/helpers/TreeViewHelper.js";
import CompareCriterionsMultiple from "@/components/compareMultiple/CompareCriterionsMultiple.vue";
import ComparisonExport from "./ComparisonExport.vue";

export default {
  components: {
    CompareCriterionsMultiple,
    ComparisonExport,
  },
  mixins: [StammdatenMixin],
  data() {
    return {
      isLoading: false,
      errorObject: null,
      selectedLanguage: "de-CH",
      languages: [
        { language: "de-CH", label: "Deutsch" },
        { language: "fr-CH", label: "Französisch" },
        { language: "it-CH", label: "Italienisch" },
      ],
      loadingContracts: 1,
      loadedContracts: [],
      contractTypes: [],
      tenants: [],
      aveStatus: [],
      allNogaBranches: [],
      versionStates: null,
      users: [],
      csvExportSettings: [
        {
          titel: "Vertrag",
          getText: (contract) => {
            return this.getContentByLanguage(
              contract.VersionInfo.versionContents,
              "nameShort",
              this.selectedLanguage,
              true,
            );
          },
        },
        {
          titel: "Name",
          getText: (contract) => {
            return this.getContentByLanguage(
              contract.VersionInfo.versionContents,
              "value",
              this.selectedLanguage,
              false,
            );
          },
        },
        {
          titel: "Kurzname",
          getText: (contract) => {
            return this.getContentByLanguage(
              contract.VersionInfo.versionContents,
              "nameShort",
              this.selectedLanguage,
              false,
            );
          },
        },
        {
          titel: "Vertragsnummer",
          getText: (contract) => {
            return contract.ContractInfo.contractNumber;
          },
        },
        {
          titel: "Version",
          getText: (contract) => {
            return contract.VersionInfo.number != null
              ? contract.VersionInfo.number.toString()
              : "";
          },
        },
        {
          titel: "Edition",
          getText: (contract) => {
            return contract.EditionInfo.id != null
              ? contract.EditionInfo.id.toString()
              : "";
          },
        },
        {
          titel: "Originalsprache",
          getText: (contract) => {
            return contract.ContractInfo.contractLanguage;
          },
        },
        {
          titel: "Vertragstyp",
          getText: (contract) => {
            return this.contractTypesfilter(
              this.contractTypes,
              contract.ContractInfo.contractTypeId,
              "id",
              "name",
            );
          },
        },
        {
          titel: "Tags (Vertrag)",
          getText: (contract) => {
            return contract.ContractInfo.tags;
          },
        },
        {
          titel: "Kommentare (Vertrag)",
          getText: (contract) => {
            return contract.ContractInfo.contractComments
              .map((x) => x.commentText)
              .join(", ");
          },
        },
        {
          titel: "Tags (Version)",
          getText: (contract) => {
            return contract.VersionInfo.tags;
          },
        },
        {
          titel: "Version Status",
          getText: (contract) => {
            return contract.VersionInfo.versionState === 0
              ? "-"
              : this.versionStates[
                  contract.VersionInfo.versionState.toString()
                ];
          },
        },
        {
          titel: "Verantwortung Erfassung",
          getText: (contract) => {
            return this.contractTypesfilter(
              this.users,
              contract.VersionInfo.responsableUserId,
              "id",
              "name",
            );
          },
        },
        {
          titel: "Kontrolle Erfassung",
          getText: (contract) => {
            return this.contractTypesfilter(
              this.users,
              contract.VersionInfo.controllerUserId,
              "id",
              "name",
            );
          },
        },
        {
          titel: "Vertrag wird übersetzt",
          getText: (contract) => {
            return contract.VersionInfo.translatedToLanguages
              .map((x) => x.language)
              .join(", ");
          },
        },
        {
          titel: "Verantwortlicher",
          getText: (contract) => {
            return contract.ContractInfo.contractResponsible;
          },
        },
        {
          titel: "Anzahl unterstellter Betriebe",
          getText: (contract) => {
            return this.CreateTextForYearValue(
              contract.ContractInfo.companiesUnderContract,
            );
          },
        },
        {
          titel: "Anzahl unterstellter ArbeitnehmerInnen",
          getText: (contract) => {
            return this.CreateTextForYearValue(
              contract.ContractInfo.employeesUnderContract,
            );
          },
        },
        {
          titel: "AVE-Status",
          getText: (contract) => {
            return this.aveStatus[contract.VersionInfo.ave.aveState];
          },
        },

        {
          titel: "Änderung gültig ab",
          getText: (contract) => {
            return this.formatDate(contract.VersionInfo.validFrom);
          },
        },
        {
          titel: "Änderung gültig bis",
          getText: (contract) => {
            return this.formatDate(contract.VersionInfo.validTo);
          },
        },
        {
          titel: "AVE",
          getText: (contract) => {
            return contract.VersionInfo.ave
              ? contract.VersionInfo.ave.aveStart ||
                contract.VersionInfo.ave.aveEnd ||
                contract.VersionInfo.ave.ablaufdatum
                ? "Ja"
                : "Nein"
              : "Nein";
          },
        },
        {
          titel: "AVE Änderungen gültig ab",
          getText: (contract) => {
            return contract.VersionInfo.ave && contract.VersionInfo.ave.aveStart
              ? this.formatDate(contract.VersionInfo.ave.aveStart)
              : "";
          },
        },
        {
          titel: "AVE Änderungen gültig bis",
          getText: (contract) => {
            return contract.VersionInfo.ave && contract.VersionInfo.ave.aveEnd
              ? this.formatDate(contract.VersionInfo.ave.aveEnd)
              : "";
          },
        },
        {
          titel: "Künstliche Verlängerung bis",
          getText: (contract) => {
            return contract.VersionInfo.ave &&
              contract.VersionInfo.ave.ablaufdatum
              ? this.formatDate(contract.VersionInfo.ave.ablaufdatum)
              : "";
          },
        },
        {
          titel: "Anhang 1",
          getText: (contract) => {
            return contract.VersionInfo.anhang1 ? "Ja" : "Nein";
          },
        },
        {
          titel: "Anhang 1 bis",
          getText: (contract) => {
            return contract.VersionInfo.anhang1ExtendedUntil
              ? this.formatDate(contract.VersionInfo.anhang1ExtendedUntil)
              : "";
          },
        },
        {
          titel: "GAV-Details anzeigen",
          getText: (contract) => {
            return contract.EditionInfo.vertragsdatenVisible ? "Ja" : "Nein";
          },
        },
        {
          titel: "Lohnrechner anzeigen",
          getText: (contract) => {
            return contract.EditionInfo.lohnrechnerVisible ? "Ja" : "Nein";
          },
        },
        {
          titel: "Üblicher Lohn anzeigen",
          getText: (contract) => {
            return contract.EditionInfo.uelVisible ? "Ja" : "Nein";
          },
        },
        {
          titel: "Kommentar zur Edition",
          getText: (contract) => {
            return contract.EditionInfo.komentar;
          },
        },
        {
          titel: "Noga-Branchen",
          getText: (contract) => {
            return this.CreateTextForNogaBranches(contract.nogaBranches);
          },
        },
        {
          titel: "Kantone",
          getText: (contract) => {
            return contract.cantons;
          },
        },
      ],
      csvTenantExportSettings: [
        {
          titel: "Publikationsdatum",
          getText: (tenant, contract) => {
            return this.formatDate(
              this.getPublicationProperty(
                contract.PublicationInfo,
                tenant.id,
                "publicationDate",
              ),
            );
          },
        },
        {
          titel: "Gültig ab",
          getText: (tenant, contract) => {
            return this.formatDate(
              this.getPublicationProperty(
                contract.PublicationInfo,
                tenant.id,
                "validFrom",
              ),
            );
          },
        },
        {
          titel: "Anzeigename",
          getText: (tenant, contract) => {
            return this.getPublicationContentProperty(
              contract.PublicationInfo,
              tenant.id,
              "value",
              this.selectedLanguage,
            );
          },
        },
        {
          titel: "Kurzname Tenant",
          getText: (tenant, contract) => {
            return this.getPublicationContentProperty(
              contract.PublicationInfo,
              tenant.id,
              "shortName",
              this.selectedLanguage,
            );
          },
        },
        {
          titel: "Kommentar",
          getText: (tenant, contract) => {
            return this.getPublicationContentProperty(
              contract.PublicationInfo,
              tenant.id,
              "comment",
              this.selectedLanguage,
            );
          },
        },
        {
          titel: "Was ist neu",
          getText: (tenant, contract) => {
            return this.getPublicationContentProperty(
              contract.PublicationInfo,
              tenant.id,
              "news",
              this.selectedLanguage,
            );
          },
        },
      ],
      pdfContent: [],
      csvContent: [],
      criterionPdf: [],
      critierionPdfDefinition: null,
      criterionCsvDefinition: null,
      fallbackLanguages: [],
      csvFileName: "export.csv",
      pdfExportName: "export.pdf",
    };
  },
  watch: {
    "$store.state.contractsToCompare"() {
      this.isLoading = true;
      this.loadedContracts = [];
      this.loadingContracts = this.$store.state.contractsToCompare.length;

      this.$store.state.contractsToCompare.forEach((contract) => {
        this.getAllContractInformation(contract);
      });
    },
    selectedLanguage() {
      if (!this.isLoading) {
        this.updateCsvContent();
        this.updatePDFContent();
      }
    },
    isLoading: function (val) {
      if (!val) {
        this.updateCsvContent();
        this.updatePDFContent();
      }
    },
  },
  mounted() {
    this.isLoading = true;
    const promises = [
      this.loadFallbackLanguages(),
      this.loadNogaBranches(),
      this.loadContractTypes(),
      this.loadTenants(),
      this.loadAveStatus(),
      this.loadVersionStates(),
      this.loadAllUsers(),
    ];

    Promise.all(promises)
      .then(() => {
        this.$store.state.contractsToCompare.forEach((contract) => {
          this.getAllContractInformation(contract);
        });
      })
      .catch((error) => {
        console.error("Fehler beim Laden der Funktionen:", error);
      });
  },
  created() {
    this.loadedContracts = [];
    this.loadingContracts = this.$store.state.contractsToCompare.length;
  },
  methods: {
    contractTypesfilter(
      filterArray,
      filterValue,
      filterProperty,
      returnProperty,
    ) {
      const returnValue = filterArray.filter((type) => {
        return type[filterProperty] === filterValue;
      })[0];

      return returnValue ? returnValue[returnProperty] : "";
    },

    updateCsvContent() {
      let csvContentMatrix = [];

      this.csvExportSettings.forEach((setting) => {
        let csvLine = [];
        csvLine.push(setting.titel);
        this.loadedContracts.forEach((contract) => {
          if (setting.getText(contract)) {
            csvLine.push(
              setting
                .getText(contract)
                .replace(/"/g, '""')
                .replace(/<br>/g, "\n"),
            );
          } else {
            csvLine.push("");
          }
        });
        csvContentMatrix.push(csvLine);
      });

      this.tenants.forEach((tenant) => {
        let csvLine = [];
        csvLine.push(tenant.bezeichnung);
        csvContentMatrix.push(csvLine);

        this.csvTenantExportSettings.forEach((setting) => {
          let csvLine = [];
          csvLine.push(setting.titel);
          this.loadedContracts.forEach((contract) => {
            if (setting.getText(tenant, contract)) {
              csvLine.push(
                setting
                  .getText(tenant, contract)
                  .replace(/"/g, '""')
                  .replace(/<br>/g, "\n"),
              );
            } else {
              csvLine.push("");
            }
          });
          csvContentMatrix.push(csvLine);
        });
      });

      if (this.criterionCsvDefinition) {
        this.criterionCsvDefinition.forEach((criterion) => {
          csvContentMatrix.push(criterion);
        });
      }

      this.csvContent = csvContentMatrix;
      if (this.loadedContracts && this.loadedContracts.length > 0) {
        this.csvFileName =
          this.loadedContracts
            .map((x) => {
              return `${x.EditionInfo.id}_`;
            })
            .reduce((a, b) => a + b) +
          this.$moment(new Date()).format("YYYYMMDD") +
          ".csv";
      }
    },
    updatePDFContent() {
      let tableBody = [];
      this.csvExportSettings.forEach((element) => {
        let rowDef = [];
        rowDef.push({ text: element.titel });
        this.loadedContracts.forEach((contract) => {
          let content = element.getText(contract);
          rowDef.push({
            text: content ? content.replaceAll("<br>", "\n") : "",
          });
        });
        tableBody.push(rowDef);
      });

      this.tenants.forEach((tenant) => {
        let subHeaderRow = [];
        subHeaderRow.push(
          ...Array(this.loadedContracts.length + 1).fill({ text: "" }),
        );
        subHeaderRow[0] = {
          text: tenant.bezeichnung,
          fontSize: 14,
          colSpan: this.loadedContracts.length + 1,
        };
        tableBody.push(subHeaderRow);
        this.csvTenantExportSettings.forEach((element) => {
          let rowDef = [];
          rowDef.push({ text: element.titel });
          this.loadedContracts.forEach((contract) => {
            rowDef.push({
              text: element.getText(tenant, contract),
            });
          });
          tableBody.push(rowDef);
        });
      });

      let headerRow = [];
      headerRow.push({ text: "" });
      this.loadedContracts.forEach((contract) => {
        headerRow.push({
          text: `"${this.csvExportSettings[0]
            .getText(contract)
            .replaceAll("<br>", "\n")}"`,
          fontSize: 16,
        });
      });

      tableBody[0] = headerRow;

      if (this.critierionPdfDefinition) {
        this.critierionPdfDefinition.forEach((row) => {
          tableBody.push(row);
        });
      }

      this.pdfContent = tableBody;
      this.pdfFileName = "";
      if (this.loadedContracts && this.loadedContracts.length > 0) {
        this.pdfFileName =
          this.loadedContracts
            .map((x) => {
              return `${x.EditionInfo.id}_`;
            })
            .reduce((a, b) => a + b) +
          this.$moment(new Date()).format("YYYYMMDD") +
          ".pdf";
      }
    },
    loadFallbackLanguages() {
      GetFallbackLanguages(
        (response) => {
          this.fallbackLanguages = response;
        },
        (error) => {
          this.loadingError = true;
          this.errorObject = error;
        },
      );
    },
    loadVersionStates() {
      GetVersionStatus(
        (response) => {
          this.versionStates = response.data;
        },
        (error) => {
          this.loadingError = true;
          this.errorObject = error;
        },
      );
    },
    loadAllUsers() {
      this.errorObject = null;

      GetAllUsers(
        (response) => {
          if (response) {
            let loadedUsers = [];
            response.forEach((user) => {
              loadedUsers.push({
                id: user.id,
                name: user.firstName + " " + user.lastName,
              });
            });
            this.users = loadedUsers;
          }
        },
        (error) => {
          this.errorObject = error;
        },
      );
    },
    updatedPdfDefinition(pdfDefinition) {
      this.critierionPdfDefinition = pdfDefinition;
      this.updatePDFContent();
      this.updateCsvContent();
    },
    updatedCsvDefinition(csvDefinition) {
      this.criterionCsvDefinition = csvDefinition;
      this.updatePDFContent();
      this.updateCsvContent();
    },
    replaceUmlaute(value) {
      value = value.replace(/&auml;/g, "ä");
      value = value.replace(/&ouml;/g, "ö");
      value = value.replace(/&uuml;/g, "ü");
      value = value.replace(/&szlig;/g, "ß");
      value = value.replace(/&Auml;/g, "Ä");
      value = value.replace(/&Ouml;/g, "Ö");
      value = value.replace(/&Uuml;/g, "Ü");
      return value;
    },
    getContentByLanguage(
      content,
      propertyName,
      language,
      returnDefaultValueIfEmpty,
    ) {
      let contentInLanguage = content.filter((content) => {
        return language === content.language;
      });

      //Wenn der Content leer ist wird die Fallbacksprache verwendet
      if (contentInLanguage.length == 0) {
        this.fallbackLanguages.forEach((fallbackLanguage) => {
          if (fallbackLanguage.isoCode == this.selectedLanguage) {
            //Suche mit der ersten fallback sprache

            contentInLanguage = content.filter((content) => {
              return (
                fallbackLanguage.isoCodeFallbackLanguage1 == content.language
              );
            });

            if (contentInLanguage.length == 0) {
              //Suche mit der zweiten fallback sprache
              contentInLanguage = content.filter((content) => {
                return (
                  fallbackLanguage.isoCodeFallbackLanguage2 == content.language
                );
              });
            }
          }
        });
      }
      //content zur ausgewählten sprache wurde gefunden
      if (contentInLanguage.length == 1) {
        if (contentInLanguage[0][propertyName]) {
          return contentInLanguage[0][propertyName];
        }
      }

      if (returnDefaultValueIfEmpty) {
        if (content[0] && content[0][[propertyName]]) {
          return content[0][[propertyName]];
        }
      }

      return "";
    },
    formatDate(date) {
      if (!date) {
        return "";
      }

      let newDate = this.$moment(date).format("DD.MM.YYYY");
      return newDate;
    },

    sortYearsValues(yearValueCollection) {
      let sortedYearValueCollection = yearValueCollection
        .slice()
        .sort((a, b) => (a.year < b.year ? 1 : a.year > b.year ? -1 : 0));

      return sortedYearValueCollection;
    },

    CreateTextForYearValue(yearValueCollection) {
      let output = "";

      //sort the yearValueCollection by year
      let sortedYearValueCollection = this.sortYearsValues(yearValueCollection);

      sortedYearValueCollection.map((yearValue) => {
        output += `${yearValue.year}, ${yearValue.statsValue};`;
      });

      return output;
    },
    CreateTextForNogaBranches(nogaBranches) {
      let output = "";
      let toplevelBranches = CreateTopLevelBranchenList(
        this.allNogaBranches,
        nogaBranches,
      );

      toplevelBranches.sort((a, b) => {
        return a - b;
      });

      toplevelBranches.map((theBranch) => {
        output += `${" - ".repeat(theBranch.path.split("/").length)} ${
          theBranch.nogaCode
        } ${theBranch.value}<br>`;
      });

      return output;
    },
    getPublicationProperty(publicationInfo, tenantId, propertyName) {
      let publication = publicationInfo.filter((publication) => {
        return publication.tenantId === tenantId;
      });

      if (publication[0] && publication[0][propertyName]) {
        return publication[0][propertyName];
      }

      return "";
    },
    getPublicationContentProperty(
      publicationInfo,
      tenantId,
      propertyName,
      language,
    ) {
      let publication = publicationInfo.filter((publication) => {
        return publication.tenantId === tenantId;
      });

      if (publication[0] && publication[0].publicationContents) {
        return this.getContentByLanguage(
          publication[0].publicationContents,
          propertyName,
          language,
          false,
        );
      }

      return "";
    },
    getAllContractInformation(contract) {
      let allContractInfo = {};

      GetSinlgeEdition(
        contract.GavNumber,
        contract.Version,
        contract.Edition,
        (response) => {
          allContractInfo.EditionInfo = response;
          this.getVersionInformation(
            allContractInfo,
            contract.GavNumber,
            contract.Version,
            contract.Edition,
          );
        },
        (error) => {
          allContractInfo.EditionInfo = {};
          this.isLoading = false;
          this.errorObject = error;
        },
      );
    },
    getVersionInformation(
      contractInfo,
      contractId,
      versionNumber,
      editionNumber,
    ) {
      GetVersionInformation(
        contractId,
        versionNumber,
        (result) => {
          contractInfo.VersionInfo = result;
          this.getContractInformation(
            contractInfo,
            contractId,
            versionNumber,
            editionNumber,
            result.id,
          );
        },
        () => {
          contractInfo.VersionInfo = {};
          this.getContractInformation(
            contractInfo,
            contractId,
            versionNumber,
            editionNumber,
            null,
          );
        },
      );
    },
    getContractInformation(
      contractInfo,
      contractId,
      versionNumber,
      editionNumber,
      versionId,
    ) {
      GetContractInformation(
        contractId,
        (result) => {
          contractInfo.ContractInfo = result;
          this.getCantons(
            contractInfo,
            contractId,
            versionNumber,
            editionNumber,
            versionId,
          );
        },
        () => {
          contractInfo.ContractInfo = {};
          this.getCantons(
            contractInfo,
            contractId,
            versionNumber,
            editionNumber,
            versionId,
          );
        },
      );
    },
    getCantons(
      contractInfo,
      contractId,
      versionNumber,
      editionNumber,
      versionId,
    ) {
      GetCantons(
        contractId,
        versionNumber,
        editionNumber,
        (response) => {
          contractInfo.cantons = "";

          if (response.length == 26) {
            contractInfo.cantons = "Alle Kantone";
          } else {
            response.map((canton) => {
              contractInfo.cantons += canton.value + "<br>";
            });
          }

          this.getNogaBranches(
            contractInfo,
            contractId,
            versionNumber,
            editionNumber,
            versionId,
          );
        },
        () => {
          contractInfo.cantons = "";
          this.getNogaBranches(
            contractInfo,
            contractId,
            versionNumber,
            editionNumber,
            versionId,
          );
        },
      );
    },
    getNogaBranches(
      contractInfo,
      contractId,
      versionNumber,
      editionNumber,
      versionId,
    ) {
      GetNogaBranchesForEdition(
        contractId,
        versionNumber,
        editionNumber,
        (response) => {
          contractInfo.nogaBranches = [];

          if (response && response.length > 0) {
            response.map((nogaBranch) => {
              contractInfo.nogaBranches.push(nogaBranch.nogaId);
            });
          }

          this.GetPublications(
            contractInfo,
            contractId,
            versionNumber,
            versionId,
          );
        },
        () => {
          this.GetPublications(
            contractInfo,
            contractId,
            versionNumber,
            versionId,
          );
        },
      );
    },
    GetPublications(contractInfo, contractId, versionNumber, versionId) {
      GetAllPublications(
        contractId,
        versionId,
        (response) => {
          contractInfo.PublicationInfo = response;
          this.SortYears(contractInfo);
        },
        () => {
          contractInfo.PublicationInfo = {};
          this.SortYears(contractInfo);
        },
      );
    },

    SortYears(contractInfo) {
      const sortCompaniesPromise = new Promise((resolve) => {
        if (
          contractInfo.companiesUnderContract &&
          contractInfo.companiesUnderContract.length
        ) {
          resolve(this.sortYearsValues(contractInfo.companiesUnderContract));
        } else {
          resolve([]);
        }
      });

      const sortEmployeesPromise = new Promise((resolve) => {
        if (
          contractInfo.employeesUnderContract &&
          contractInfo.employeesUnderContract.length
        ) {
          resolve(this.sortYearsValues(contractInfo.employeesUnderContract));
        } else {
          resolve([]);
        }
      });

      Promise.all([sortCompaniesPromise, sortEmployeesPromise])
        .then(([sortedCompanies, sortedEmployees]) => {
          contractInfo.companiesUnderContract = sortedCompanies;
          contractInfo.employeesUnderContract = sortedEmployees;
          this.setAllContractValues(contractInfo);
        })
        .catch((error) => {
          console.error("An error occurred during sorting:", error);
        });
    },

    setAllContractValues(contractInfo) {
      this.loadedContracts.push(contractInfo);
      this.loadingContracts = this.loadingContracts - 1;
      if (this.loadingContracts <= 0) {
        this.loadedContracts = this.loadedContracts.slice().sort((a, b) => {
          return a.EditionInfo.id - b.EditionInfo.id;
        });
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.compareblaTable thead tr td {
  position: sticky;
  top: 0;
  background-color: #dbdbdb !important;
}

.compareblaTable tbody tr td:first-child {
  position: -webkit-sticky;
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: #505050;
  color: white;
}

.compareblaTable tbody tr td:nth-child(even) {
  background-color: #f7f7f7;
}

.compareblaTable tbody tr td {
  width: 500px;
}

.compareblaTable tbody .newSection td {
  background-color: #777777 !important;
}

.compareblaTable {
  table-layout: fixed;
  position: relative;
}

.compareblaTableWrapper {
  max-height: calc(100vh - 200px);
  overflow: scroll;
  position: relative;
}

@media print {
  @page {
    size: landscape;
  }

  .compareblaTableWrapper {
    position: relative;
    overflow: auto;
    max-height: initial;
    width: 100%;
  }

  .compareblaTable {
    position: initial;
    width: 100%;
    min-width: 100% !important;
  }

  .compareblaTable tbody tr td {
    width: auto;
  }

  .compareblaTable thead tr td {
    position: initial;
    top: initial;
  }

  .compareblaTable tbody tr td:first-child {
    position: initial;
    position: initial;
    left: initial;
    color: black;
  }
}
</style>
